const FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
const ZOOM_VIEWPORT_CONTENT = 'width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes';
const MIN_MEDIA_ZOOM_SCALE = 1;
const MAX_MEDIA_ZOOM_SCALE = 4;
const DOUBLE_TAP_ZOOM_SCALE = 2;
const DOUBLE_TAP_DELAY = 300;
const setBackgroundInert = (element, isInert) => {
  let node = element;
  while (node && node !== document.body) {
    const parent = node.parentElement;
    if (parent) {
      Array.from(parent.children).forEach(sibling => {
        if (sibling !== node) sibling.inert = isInert;
      });
    }
    node = parent;
  }
};
const initMediaZoom = lightbox => {
  let activeMedia = null;
  let scale = MIN_MEDIA_ZOOM_SCALE;
  let translateX = 0;
  let translateY = 0;
  let startDistance = 0;
  let startScale = MIN_MEDIA_ZOOM_SCALE;
  let startTranslateX = 0;
  let startTranslateY = 0;
  let startPointerX = 0;
  let startPointerY = 0;
  let isPanning = false;
  let lastTapTime = 0;
  const getMedia = target => {
    const slide = target.closest('[data-gallery-slide]');
    if (!slide || slide.hidden) return null;
    return slide.querySelector('img.c-gallery__lightbox-media, video.c-gallery__lightbox-media');
  };
  const applyTransform = () => {
    if (!activeMedia) return;
    activeMedia.style.transform = scale > MIN_MEDIA_ZOOM_SCALE ? `translate(${translateX}px, ${translateY}px) scale(${scale})` : '';
    activeMedia.classList.toggle('is-zoomed', scale > MIN_MEDIA_ZOOM_SCALE);
  };
  const clampTranslate = () => {
    if (!activeMedia) return;
    const rect = activeMedia.getBoundingClientRect();
    const maxX = Math.max(0, rect.width * (scale - 1) / (2 * scale));
    const maxY = Math.max(0, rect.height * (scale - 1) / (2 * scale));
    translateX = Math.min(maxX, Math.max(-maxX, translateX));
    translateY = Math.min(maxY, Math.max(-maxY, translateY));
  };
  const reset = () => {
    scale = MIN_MEDIA_ZOOM_SCALE;
    translateX = 0;
    translateY = 0;
    isPanning = false;
    if (activeMedia) {
      activeMedia.style.transform = '';
      activeMedia.classList.remove('is-zoomed');
    }
    activeMedia = null;
  };
  const getDistance = _ref => {
    let [a, b] = _ref;
    return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
  };
  const onTouchStart = event => {
    if (!lightbox.classList.contains('is-open')) return;
    const media = getMedia(event.target);
    if (!media) return;
    if (activeMedia && activeMedia !== media) reset();
    activeMedia = media;
    if (event.touches.length === 2) {
      event.preventDefault();
      isPanning = false;
      startDistance = getDistance(event.touches);
      startScale = scale;
      startTranslateX = translateX;
      startTranslateY = translateY;
    } else if (event.touches.length === 1) {
      const now = Date.now();
      if (now - lastTapTime < DOUBLE_TAP_DELAY) {
        lastTapTime = 0;
        scale = scale > MIN_MEDIA_ZOOM_SCALE ? MIN_MEDIA_ZOOM_SCALE : DOUBLE_TAP_ZOOM_SCALE;
        translateX = 0;
        translateY = 0;
        applyTransform();
        return;
      }
      lastTapTime = now;
      if (scale > MIN_MEDIA_ZOOM_SCALE) {
        isPanning = true;
        startPointerX = event.touches[0].clientX - translateX;
        startPointerY = event.touches[0].clientY - translateY;
      }
    }
  };
  const onTouchMove = event => {
    if (!activeMedia) return;
    if (event.touches.length === 2) {
      event.preventDefault();
      const distance = getDistance(event.touches);
      scale = Math.min(MAX_MEDIA_ZOOM_SCALE, Math.max(MIN_MEDIA_ZOOM_SCALE, startScale * (distance / startDistance)));
      translateX = startTranslateX;
      translateY = startTranslateY;
      clampTranslate();
      applyTransform();
    } else if (event.touches.length === 1 && isPanning) {
      event.preventDefault();
      translateX = event.touches[0].clientX - startPointerX;
      translateY = event.touches[0].clientY - startPointerY;
      clampTranslate();
      applyTransform();
    }
  };
  const onTouchEnd = event => {
    if (event.touches.length === 0) {
      isPanning = false;
      if (scale <= MIN_MEDIA_ZOOM_SCALE) reset();
    }
  };
  lightbox.addEventListener('touchstart', onTouchStart, {
    passive: false
  });
  lightbox.addEventListener('touchmove', onTouchMove, {
    passive: false
  });
  lightbox.addEventListener('touchend', onTouchEnd);
  lightbox.addEventListener('touchcancel', onTouchEnd);
  return {
    reset
  };
};
const stopSlideMedia = slide => {
  const video = slide.querySelector('video');
  if (video) {
    video.pause();
  }
  const iframe = slide.querySelector('iframe');
  if (iframe && iframe.getAttribute('src')) {
    iframe.setAttribute('src', iframe.getAttribute('src'));
  }
};
const initGallery = root => {
  const triggers = Array.from(root.querySelectorAll('[data-gallery-trigger]'));
  const lightbox = root.querySelector('[data-gallery-lightbox]');
  if (!triggers.length || !lightbox) return;
  const slides = Array.from(lightbox.querySelectorAll('[data-gallery-slide]'));
  const titleEl = lightbox.querySelector('[data-gallery-title]');
  const counterEl = lightbox.querySelector('[data-gallery-counter]');
  const counterTemplate = lightbox.dataset.galleryCounterTemplate || '{current} / {total}';
  const prevBtn = lightbox.querySelector('[data-gallery-prev]');
  const nextBtn = lightbox.querySelector('[data-gallery-next]');
  const closeEls = Array.from(lightbox.querySelectorAll('[data-gallery-close]'));
  const dialog = lightbox.querySelector('.c-gallery__lightbox-dialog');
  const viewportMeta = document.querySelector('meta[name="viewport"]');
  const defaultViewportContent = viewportMeta ? viewportMeta.getAttribute('content') : null;
  const mediaZoom = initMediaZoom(lightbox);
  if (!slides.length) return;
  if (slides.length <= 1) {
    if (prevBtn) prevBtn.hidden = true;
    if (nextBtn) nextBtn.hidden = true;
  }
  let currentIndex = 0;
  let lastFocusedTrigger = null;
  const showSlide = index => {
    currentIndex = (index % slides.length + slides.length) % slides.length;
    mediaZoom.reset();
    slides.forEach((slide, i) => {
      const isActive = i === currentIndex;
      slide.hidden = !isActive;
      slide.classList.toggle('is-active', isActive);
      if (!isActive) stopSlideMedia(slide);
    });
    const activeSlide = slides[currentIndex];
    const title = activeSlide.dataset.slideTitle || '';
    if (titleEl) {
      titleEl.textContent = title;
      titleEl.hidden = !title;
    }
    if (counterEl) {
      counterEl.textContent = counterTemplate.replace('{current}', currentIndex + 1).replace('{total}', slides.length);
    }
  };
  const trapFocus = event => {
    if (event.key !== 'Tab' || !dialog) return;
    const focusable = Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter(el => !el.hidden && el.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
  const closeLightbox = () => {
    lightbox.classList.remove('is-open');
    document.body.classList.remove('c-gallery-lightbox-open');
    slides.forEach(stopSlideMedia);
    lightbox.hidden = true;
    setBackgroundInert(lightbox, false);
    mediaZoom.reset();
    if (viewportMeta && defaultViewportContent !== null) {
      viewportMeta.setAttribute('content', defaultViewportContent);
    }
    if (lastFocusedTrigger) lastFocusedTrigger.focus();
  };
  const openLightbox = (index, triggerEl) => {
    lastFocusedTrigger = triggerEl || null;
    showSlide(index);
    lightbox.hidden = false;
    setBackgroundInert(lightbox, true);
    document.body.classList.add('c-gallery-lightbox-open');
    window.requestAnimationFrame(() => lightbox.classList.add('is-open'));
    if (viewportMeta) {
      viewportMeta.setAttribute('content', ZOOM_VIEWPORT_CONTENT);
    }
    const closeBtn = lightbox.querySelector('.c-gallery__lightbox-close');
    if (closeBtn) closeBtn.focus();
  };
  document.addEventListener('keydown', event => {
    if (lightbox.hidden) return;
    if (event.key === 'Escape') {
      closeLightbox();
    } else if (event.key === 'ArrowRight') {
      showSlide(currentIndex + 1);
    } else if (event.key === 'ArrowLeft') {
      showSlide(currentIndex - 1);
    } else {
      trapFocus(event);
    }
  });
  triggers.forEach(trigger => {
    trigger.addEventListener('click', () => {
      const index = parseInt(trigger.dataset.galleryIndex, 10) || 0;
      openLightbox(index, trigger);
    });
  });
  closeEls.forEach(el => {
    el.addEventListener('click', closeLightbox);
  });
  if (prevBtn) {
    prevBtn.addEventListener('click', () => showSlide(currentIndex - 1));
  }
  if (nextBtn) {
    nextBtn.addEventListener('click', () => showSlide(currentIndex + 1));
  }
};
const gallery = () => {
  document.querySelectorAll('[data-gallery]').forEach(initGallery);
};
export default gallery;